import { Avatar, Button, Divider } from "@fluentui/react-components";
import {
  Document20Regular,
  SignOut20Regular,
  Warning20Regular,
} from "@fluentui/react-icons";
import designTokens from "theme/customTheme";
import { IUserProfileMenuProps } from "./UserProfileMenu.types";
import { useStyles } from "./UserProfileMenu.styles";
import { useCallback, useEffect, useRef } from "react";

export const UserProfileMenu = (props: IUserProfileMenuProps): JSX.Element => {
  const styles = useStyles();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuRef.current) return;
    const firstButton =
      menuRef.current.querySelector<HTMLButtonElement>("button");
    firstButton?.focus();
  }, []);

  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!menuRef.current) return;
      const buttons = Array.from(
        menuRef.current.querySelectorAll<HTMLButtonElement>("button"),
      );
      const currentIndex = buttons.indexOf(e.target as HTMLButtonElement);
      if (currentIndex === -1) return;

      let nextIndex: number | null = null;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
      } else if (e.key === "ArrowUp") {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
      } else if (e.key === "Home") {
        nextIndex = 0;
      } else if (e.key === "End") {
        nextIndex = buttons.length - 1;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        buttons[nextIndex].focus();
      }
    },
    [],
  );

  return (
    <div className={styles.userMenuWrapper}>
      <div
        ref={menuRef}
        role="menu"
        aria-label="User profile menu"
        className={styles.userMenuPanel}
        onKeyDown={handleMenuKeyDown}
      >
        <div className={styles.userMenuHeader}>
          <Avatar
            name={props.currentUser.displayName}
            size={designTokens.avatarSize.lg}
            initials={{
              style: {
                backgroundColor: designTokens.accent[500],
                color: designTokens.neutral[50],
              },
            }}
          />
          <div className={styles.userMenuHeaderInfo}>
            <div className={styles.userMenuHeaderName}>
              {props.currentUser.displayName}
            </div>
          </div>
        </div>
        <Divider className={styles.userMenuDivider} />
        <Button
          role="menuitem"
          className={styles.userMenuItem}
          icon={<Warning20Regular className={styles.userMenuItemIcon} />}
          onClick={() => props.onReportAnIssue()}
        >
          Report an Issue
        </Button>
        <Button
          role="menuitem"
          className={styles.userMenuItem}
          icon={<Document20Regular className={styles.userMenuItemIcon} />}
          onClick={() => props.onAcceptableUsePolicy()}
          aria-label="Acceptable Use Policy (opens in new tab)"
        >
          Acceptable Use Policy
        </Button>
        <Divider className={styles.userMenuDivider} />
        <Button
          role="menuitem"
          className={styles.userMenuLogout}
          icon={<SignOut20Regular className={styles.logoutIcon} />}
          onClick={() => props.onLogout()}
        >
          Logout
        </Button>
      </div>
    </div>
  );
};
